import React, { useState, useEffect } from 'react';
import { Save, Plus, X, RotateCcw, Globe, Tag, FileText, FolderPlus, Trash2 } from 'lucide-react';
import { API_URL } from '../../config';

export default function SeoKeywordsAdmin() {
  const [categories, setCategories] = useState({});
  const [metaDescription, setMetaDescription] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [ogDescription, setOgDescription] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  useEffect(() => {
    loadSeoKeywords();
  }, []);

  // Auto-select first category when categories change
  useEffect(() => {
    const cats = Object.keys(categories);
    if (cats.length > 0 && (!selectedCategory || !categories[selectedCategory])) {
      setSelectedCategory(cats[0]);
    }
  }, [categories, selectedCategory]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('snapshot_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const loadSeoKeywords = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/seo-keywords`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setCategories(data.categories || {});
      setMetaDescription(data.meta_description || '');
      setMetaTitle(data.meta_title || '');
      setOgDescription(data.og_description || '');
      setUpdatedAt(data.updated_at);
      setUpdatedBy(data.updated_by);
    } catch (error) {
      console.error('Error loading SEO keywords:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der SEO Keywords' });
    } finally {
      setLoading(false);
    }
  };

  const saveSeoKeywords = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/admin/seo-keywords`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          categories,
          meta_description: metaDescription,
          meta_title: metaTitle,
          og_description: ogDescription
        })
      });
      if (!res.ok) throw new Error('Failed to save');
      const data = await res.json();
      setCategories(data.categories);
      setMetaDescription(data.meta_description);
      setMetaTitle(data.meta_title);
      setOgDescription(data.og_description);
      setUpdatedAt(data.updated_at);
      setUpdatedBy(data.updated_by);
      setMessage({ type: 'success', text: 'SEO Keywords gespeichert!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving SEO keywords:', error);
      setMessage({ type: 'error', text: 'Fehler beim Speichern' });
    } finally {
      setSaving(false);
    }
  };

  const addKeyword = () => {
    const keyword = newKeyword.trim();
    if (!keyword || !selectedCategory) return;
    const existing = categories[selectedCategory] || [];
    if (existing.some(k => k.toLowerCase() === keyword.toLowerCase())) return;
    setCategories({
      ...categories,
      [selectedCategory]: [...existing, keyword]
    });
    setNewKeyword('');
  };

  const removeKeyword = (category, index) => {
    const updated = { ...categories };
    updated[category] = updated[category].filter((_, i) => i !== index);
    if (updated[category].length === 0) {
      delete updated[category];
      if (selectedCategory === category) {
        setSelectedCategory(Object.keys(updated)[0] || '');
      }
    }
    setCategories(updated);
  };

  const addCategory = () => {
    const name = newCategoryName.trim();
    if (!name || categories[name]) return;
    setCategories({ ...categories, [name]: [] });
    setSelectedCategory(name);
    setNewCategoryName('');
  };

  const deleteCategory = (category) => {
    if (!window.confirm(`Kategorie "${category}" und alle Keywords loeschen?`)) return;
    const updated = { ...categories };
    delete updated[category];
    setCategories(updated);
    if (selectedCategory === category) {
      setSelectedCategory(Object.keys(updated)[0] || '');
    }
  };

  const handleKeyDown = (e, fn) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fn();
    }
  };

  // Count total keywords
  const totalKeywords = Object.values(categories).reduce((sum, kws) => sum + kws.length, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Globe className="w-7 h-7 text-[#10B981]" />
            SEO Keywords & Meta Tags
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Verwalte SEO Keywords und Meta-Beschreibungen fuer bessere Suchmaschinen-Rankings.
          </p>
          {updatedAt && (
            <p className="text-white/30 text-xs mt-1">
              Zuletzt aktualisiert: {new Date(updatedAt).toLocaleString('de-DE')}
              {updatedBy && ` von ${updatedBy}`}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadSeoKeywords}
            className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-xl transition-all flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reload
          </button>
          <button
            onClick={saveSeoKeywords}
            disabled={saving}
            className="px-6 py-2 bg-[#10B981] hover:bg-[#0d9668] text-black font-semibold rounded-xl transition-all flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Status Message */}
      {message && (
        <div className={`p-4 rounded-xl border ${
          message.type === 'success'
            ? 'bg-green-500/10 border-green-500/30 text-green-400'
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {message.text}
        </div>
      )}

      {/* Meta Tags Section */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-blue-400" />
          Meta Tags
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-white/60 text-sm mb-1">Meta Title (Browser-Tab & Suchergebnis-Titel)</label>
            <input
              type="text"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              placeholder="SnapshotPro - KI-Avatare & Videos erstellen"
              className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#10B981]/50"
            />
            <p className="text-white/30 text-xs mt-1">{metaTitle.length}/60 Zeichen (empfohlen: max. 60)</p>
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-1">Meta Description (Suchergebnis-Beschreibung)</label>
            <textarea
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              placeholder="Erstelle fotorealistische KI-Avatare und Videos..."
              rows={3}
              className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#10B981]/50 resize-none"
            />
            <p className="text-white/30 text-xs mt-1">{metaDescription.length}/160 Zeichen (empfohlen: max. 160)</p>
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-1">Open Graph Description (Social Media Teilen)</label>
            <textarea
              value={ogDescription}
              onChange={(e) => setOgDescription(e.target.value)}
              placeholder="Erstelle fotorealistische KI-Avatare und Videos in Sekunden..."
              rows={2}
              className="w-full px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#10B981]/50 resize-none"
            />
          </div>
        </div>
      </div>

      {/* SEO Keywords by Category */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Tag className="w-5 h-5 text-[#10B981]" />
            SEO Keywords
            <span className="text-sm font-normal text-white/40 ml-2">({totalKeywords} Keywords in {Object.keys(categories).length} Kategorien)</span>
          </h3>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.keys(categories).map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                selectedCategory === cat
                  ? 'bg-[#10B981] text-black'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              {cat}
              <span className="text-xs opacity-60">({(categories[cat] || []).length})</span>
              <button
                onClick={(e) => { e.stopPropagation(); deleteCategory(cat); }}
                className="ml-1 opacity-40 hover:opacity-100"
                title="Kategorie loeschen"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </button>
          ))}

          {/* Add Category */}
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, addCategory)}
              placeholder="Neue Kategorie..."
              className="px-3 py-1.5 bg-black/50 border border-white/20 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#10B981]/50 w-40"
            />
            <button
              onClick={addCategory}
              disabled={!newCategoryName.trim()}
              className="p-1.5 bg-[#10B981]/20 hover:bg-[#10B981]/30 text-[#10B981] border border-[#10B981]/30 rounded-lg transition-all disabled:opacity-30"
              title="Kategorie hinzufuegen"
            >
              <FolderPlus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Keywords for selected category */}
        {selectedCategory && categories[selectedCategory] !== undefined && (
          <>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, addKeyword)}
                placeholder={`Neues Keyword fuer "${selectedCategory}"...`}
                className="flex-1 px-4 py-2 bg-black/50 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#10B981]/50"
              />
              <button
                onClick={addKeyword}
                disabled={!newKeyword.trim()}
                className="px-4 py-2 bg-[#10B981]/20 hover:bg-[#10B981]/30 text-[#10B981] border border-[#10B981]/30 rounded-xl transition-all flex items-center gap-1 disabled:opacity-30"
              >
                <Plus className="w-4 h-4" />
                Hinzufuegen
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {(categories[selectedCategory] || []).map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#10B981]/10 border border-[#10B981]/20 text-[#10B981] text-sm rounded-lg group hover:border-[#10B981]/40 transition-all"
                >
                  {keyword}
                  <button
                    onClick={() => removeKeyword(selectedCategory, index)}
                    className="opacity-50 hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
              {(categories[selectedCategory] || []).length === 0 && (
                <p className="text-white/30 text-sm italic">Keine Keywords in dieser Kategorie.</p>
              )}
            </div>
          </>
        )}

        {Object.keys(categories).length === 0 && (
          <p className="text-white/30 text-sm italic">Keine Kategorien vorhanden. Erstelle eine neue Kategorie oben.</p>
        )}
      </div>

      {/* Preview */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Google Suchergebnis Vorschau</h3>
        <div className="bg-white rounded-xl p-4 max-w-xl">
          <p className="text-[#1a0dab] text-lg font-medium truncate">
            {metaTitle || 'SnapshotPro - KI-Avatare & Videos erstellen'}
          </p>
          <p className="text-[#006621] text-sm">https://snapshotpro.ai</p>
          <p className="text-[#545454] text-sm mt-1 line-clamp-2">
            {metaDescription || 'Erstelle fotorealistische KI-Avatare und Videos...'}
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-5">
        <h4 className="text-blue-400 font-semibold mb-2">Wie funktioniert es?</h4>
        <ul className="text-white/50 text-sm space-y-1.5">
          <li><strong className="text-white/70">Meta Tags:</strong> Title und Description werden in den HTML-Head der Website injiziert und von Suchmaschinen indexiert.</li>
          <li><strong className="text-white/70">Keywords:</strong> Werden als meta keywords Tag eingefuegt. Kategorien helfen bei der Organisation.</li>
          <li><strong className="text-white/70">Open Graph:</strong> OG Description wird angezeigt, wenn jemand die Seite auf Social Media teilt.</li>
          <li><strong className="text-white/70">Cache:</strong> Aenderungen werden nach max. 5 Minuten wirksam (Backend-Cache).</li>
          <li><strong className="text-white/70">Tipp:</strong> Meta Title max. 60 Zeichen, Meta Description max. 160 Zeichen fuer optimale Darstellung in Google.</li>
        </ul>
      </div>
    </div>
  );
}
